import { useState, useEffect, useRef } from 'react';
import { LIMITES } from '../../limites';
import './MiniGameModal.css';
// 👇 IMPORTS DE LOS NUEVOS ÓRGANOS DEL MICRÓFONO
import { calcSimilarity } from './micData';
import { useMicSystem } from './useMicSystem';
import MicDisplay from './MicDisplay';

// ─────────────────────────────────────────────────────────────
//  PURE HELPERS  (defined outside so they never get re-created)
// ─────────────────────────────────────────────────────────────
function shuffle(arr) {
  return [...arr].sort(() => Math.random() - 0.5);
}

// ❌ La función calcSimilarity se borra porque ya viene importada desde './micData'

function getOptions(word, allWords) {
  const wrong = shuffle(allWords.filter((w) => w.en !== word.en)).slice(0, 3);
  return shuffle([word, ...wrong]);
}

function buildQuestions(modeId, words) {
  const pool = shuffle(words).slice(0, 20);
  
  // 🚀 INYECCIÓN EXACTA: Agregamos 'mic' a la batidora del Ultimate Mix
  const MIX = ['listen', 'sniper', 'spell', 'mic', 'lightning']; 
  
  return pool.map((word, i) => ({
    word,
    mode: modeId === 'mix' ? MIX[i % MIX.length] : modeId,
    opts: getOptions(word, words),
  }));
}

// ─────────────────────────────────────────────────────────────
//  STATIC CONFIG
// ─────────────────────────────────────────────────────────────
const MODES = [
  {
    id: 'listen',
    icon: '👂',
    phase: 'PHASE 1',
    name: 'Bionic Ear',
    color: '#6366f1',
    desc: 'Audio plays — choose the correct Spanish translation. Train your ear before your eyes.',
    tags: ['Listening', 'Recognition', 'Audio'],
  },
  {
    id: 'sniper',
    icon: '👁️',
    phase: 'PHASE 2',
    name: 'Sniper Vision',
    color: '#f59e0b',
    desc: 'Spanish word flashes. Hit the correct English before the timer explodes.',
    tags: ['Speed', 'Reading', 'Timer'],
  },
  {
    id: 'spell',
    icon: '⌨️',
    phase: 'PHASE 3',
    name: 'Muscle Memory',
    color: '#10b981',
    desc: 'See the Spanish + phonetics. Type the exact English spelling. No options — only memory.',
    tags: ['Spelling', 'Writing', 'Precision'],
  },
  {
    id: 'mic',
    icon: '🎙️',
    phase: 'PHASE 4',
    name: 'Pronunciation Match',
    color: '#f43f5e',
    desc: 'See the word. Say it out loud. The browser listens. Speak like a native!',
    tags: ['Speaking', 'Mic', 'Pronunciation'],
  },
  {
    id: 'lightning',
    icon: '⚡',
    phase: 'BONUS',
    name: 'Lightning Round',
    color: '#06b6d4',
    desc: 'Rapid fire combos. Answer as fast as possible — build your combo multiplier!',
    tags: ['Speed', 'Combos', 'Arcade'],
  },
  {
    id: 'mix',
    icon: '🌪️',
    phase: 'MIX',
    name: 'Ultimate Mix',
    color: '#a3e635', // 👈 ¡EL ANTÍDOTO! Verde Limón Neón (antes era #a78bfa - morado)
    desc: 'All 4 modes shuffle randomly. One session to rule them all.',
    tags: ['All Modes', 'Surprise', 'Expert'],
    isMix: true,
  },
];

// ─────────────────────────────────────────────────────────────
//  COMPONENT
// ─────────────────────────────────────────────────────────────
/**
 * MiniGameModal
 * @param {Array}    words   – [{ en, es, ph }, …]
 * @param {Function} onClose – called when the user dismisses the modal
 * @param {boolean}  isDark  – inject dark-mode palette
 */
export default function MiniGameModal({ words, onClose, isDark: propIsDark }) {
  // 🚀 RADAR DE MODO OSCURO: Detecta el tema local O el global de tu página
  const isDark =
    propIsDark ||
    document.documentElement.getAttribute('data-theme') === 'dark' ||
    document.body.getAttribute('data-theme') === 'dark' ||
    document.querySelector('.glossary-root')?.getAttribute('data-theme') === 'dark';

  // ── screens ────────────────────────────────────────────────
  const [screen, setScreen] = useState('home'); // 'home' | 'game' | 'results'

  // ── game state ─────────────────────────────────────────────
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(5);
  const [streak, setStreak] = useState(0);
  const [maxStreak, setMaxStreak] = useState(0);
  const [results, setResults] = useState([]);
  const [combo, setCombo] = useState(1);
  const [currentModeId, setCurrentModeId] = useState(null);
  const [lastModeId, setLastModeId] = useState(null);

  // ── timer ──────────────────────────────────────────────────
  const [timerDisplay, setTimerDisplay] = useState(5);
  const [timerPct, setTimerPct] = useState(100);
  const timerRef = useRef(null);
  const timerLeftRef = useRef(5);
  const timerSecsRef = useRef(5);

  // ── answer (MCQ) ───────────────────────────────────────────
  // { chosen: string|null, correct: string, result: bool }
  const [answerState, setAnswerState] = useState(null);
  // ref for stale-closure safe checks inside timer/timeout callbacks
  const answeredRef = useRef(false);

  // ── spell ──────────────────────────────────────────────────
  const [spellValue, setSpellValue] = useState('');
  const [spellFeedback, setSpellFeedback] = useState({ text: '', ok: null });
  const [spellShake, setSpellShake] = useState(false);
  const [spellCorrect, setSpellCorrect] = useState(false);
  const spellInputRef = useRef(null);

  // ❌ ELIMINADO: micStatus, micResult, recognizingRef y toggleMic

  // ── listen ─────────────────────────────────────────────────
  const [isSpeaking, setIsSpeaking] = useState(false);

  // ── visuals ────────────────────────────────────────────────
  const [flashColor, setFlashColor] = useState(null);
  const [floatScores, setFloatScores] = useState([]);

  // ── canvas refs ────────────────────────────────────────────
  const particlesRef = useRef(null);
  const confettiRef = useRef(null);
  const mountedRef = useRef(true);

  // ── game-state refs  (needed inside setInterval / setTimeout callbacks
  //    to avoid stale closures — always kept in sync with their state twin)
  const questionsRef = useRef([]);
  const currentIdxRef = useRef(0);
  const livesRef = useRef(5);
  const scoreRef = useRef(0);
  const streakRef = useRef(0);
  const maxStreakRef = useRef(0);
  const resultsRef = useRef([]);
  const comboRef = useRef(1);

  // sync refs ↔ state
  useEffect(() => {
    questionsRef.current = questions;
  }, [questions]);
  useEffect(() => {
    currentIdxRef.current = currentIndex;
  }, [currentIndex]);
  useEffect(() => {
    livesRef.current = lives;
  }, [lives]);
  useEffect(() => {
    scoreRef.current = score;
  }, [score]);
  useEffect(() => {
    streakRef.current = streak;
  }, [streak]);
  useEffect(() => {
    maxStreakRef.current = maxStreak;
  }, [maxStreak]);
  useEffect(() => {
    resultsRef.current = results;
  }, [results]);
  useEffect(() => {
    comboRef.current = combo;
  }, [combo]);

  // ── mount / unmount ────────────────────────────────────────
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopTimer();
      window.speechSynthesis?.cancel();
    };
  }, []);

  // ── particles canvas ───────────────────────────────────────
  // ── particles & shooting stars canvas ──────────────────────
  // ── particles + subtle shooting stars canvas ────────────────────
  useEffect(() => {
    const canvas = particlesRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;

    // ─── PARTÍCULAS CON TWINKLE ──────────────────────────────────
    const pts = Array.from({ length: 65 }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      r: Math.random() * 2.2 + 0.5,
      dx: (Math.random() - 0.5) * 0.28,
      dy: (Math.random() - 0.5) * 0.28,
      baseAlpha: Math.random() * 0.35 + 0.1,
      twinkleSpeed: Math.random() * 0.025 + 0.006,
      twinklePhase: Math.random() * Math.PI * 2,
    }));

    // ─── SHOOTING STARS ─────────────────────────────────────────
    let stars = [];

    // Cooldown: evita que nazcan dos seguidas muy rápido
    let spawnCooldown = 0;

    function spawnStar() {
      const BASE_ANGLE = (Math.PI * 3) / 4; // 135° — abajo-izquierda
      const angleRad = BASE_ANGLE + (Math.random() - 0.5) * 0.4;
      const speed = Math.random() * 5 + 7; // más lentas → más elegantes

      stars.push({
        x: canvas.width * 0.55 + Math.random() * canvas.width * 0.5,
        y: Math.random() * canvas.height * 0.4 - 40,
        vx: Math.cos(angleRad) * speed,
        vy: Math.sin(angleRad) * speed,
        length: Math.random() * 140 + 80, // colas largas y elegantes
        opacity: 0,
        maxOp: Math.random() * 0.28 + 0.18, // ★ más tenues (0.18–0.46)
        phase: 'in',
        lineW: isDark ? 0.9 : 1.2, // ★ más delgadas
      });
    }

    function resize() {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    }
    resize();
    window.addEventListener('resize', resize);

    const pColorRGB = isDark ? '130,130,255' : '99,102,241';
    const sColorRGB = isDark ? '210,210,255' : '99,102,241';

    function draw() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // ══ PARTÍCULAS ══════════════════════════════════════════════
      pts.forEach((p) => {
        p.twinklePhase += p.twinkleSpeed;
        const tw = 0.65 + Math.sin(p.twinklePhase) * 0.35;
        const alpha = p.baseAlpha * tw * (isDark ? 1 : 1.6);
        const r = p.r * (0.75 + tw * 0.45);

        ctx.shadowColor = `rgba(${pColorRGB}, ${alpha * 0.7})`;
        ctx.shadowBlur = r * 4;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${pColorRGB}, ${alpha})`;
        ctx.fill();

        p.x += p.dx;
        p.y += p.dy;
        if (p.x < 0 || p.x > canvas.width) p.dx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.dy *= -1;
      });
      ctx.shadowBlur = 0;

      // ══ SPAWN — máx 1 simultánea, cooldown ~5 seg entre apariciones ══
      spawnCooldown--;
      if (spawnCooldown <= 0 && stars.length === 0 && Math.random() < 0.004) {
        spawnStar();
        // cooldown de 280–420 frames (~4.7–7 seg a 60fps) → aparición esporádica
        spawnCooldown = Math.floor(Math.random() * 140 + 280);
      }

      // ══ DIBUJAR SHOOTING STARS ══════════════════════════════════
      stars.forEach((s) => {
        if (s.phase === 'in') {
          s.opacity += 0.04; // fade-in más lento
          if (s.opacity >= s.maxOp) {
            s.opacity = s.maxOp;
            s.phase = 'out';
          }
        } else {
          s.opacity -= 0.007; // fade-out muy suave
        }

        const mag = Math.hypot(s.vx, s.vy);
        const nx = s.vx / mag;
        const ny = s.vy / mag;
        const tailX = s.x - nx * s.length;
        const tailY = s.y - ny * s.length;

        const grad = ctx.createLinearGradient(s.x, s.y, tailX, tailY);
        grad.addColorStop(0, `rgba(${sColorRGB}, ${s.opacity})`);
        grad.addColorStop(0.3, `rgba(${sColorRGB}, ${s.opacity * 0.4})`);
        grad.addColorStop(0.75, `rgba(${sColorRGB}, ${s.opacity * 0.08})`);
        grad.addColorStop(1, `rgba(${sColorRGB}, 0)`);

        ctx.beginPath();
        ctx.moveTo(s.x, s.y);
        ctx.lineTo(tailX, tailY);
        ctx.strokeStyle = grad;
        ctx.lineWidth = s.lineW;
        ctx.lineCap = 'round';
        ctx.shadowColor = `rgba(${sColorRGB}, ${s.opacity * 0.4})`;
        ctx.shadowBlur = isDark ? 4 : 7;
        ctx.stroke();
        ctx.shadowBlur = 0;

        // Cabeza: punto pequeño y discreto
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.lineW * 1.2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${sColorRGB}, ${s.opacity})`;
        ctx.shadowColor = `rgba(${sColorRGB}, ${s.opacity * 0.7})`;
        ctx.shadowBlur = isDark ? 8 : 12;
        ctx.fill();
        ctx.shadowBlur = 0;

        s.x += s.vx;
        s.y += s.vy;
      });

      stars = stars.filter((s) => s.opacity > 0 && s.x > -200 && s.y < canvas.height + 120);

      animId = requestAnimationFrame(draw);
    }
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, [isDark]);

  // ── listen auto-play + sniper timer ────────────────────────
  useEffect(() => {
    if (screen !== 'game') return;
    const q = questionsRef.current[currentIndex];
    if (!q) return;
    answeredRef.current = false;

    if (q.mode === 'listen') {
      const t = setTimeout(() => speakWord(q.word.en), 300);
      return () => clearTimeout(t);
    }
    if (q.mode === 'sniper') {
      startTimer(5);
      return () => stopTimer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, screen]);

  // ─────────────────────────────────────────────────────────
  //  TIMER
  // ─────────────────────────────────────────────────────────
  function stopTimer() {
    clearInterval(timerRef.current);
    timerRef.current = null;
  }

  function startTimer(secs) {
    stopTimer();
    timerLeftRef.current = secs;
    timerSecsRef.current = secs;
    setTimerDisplay(secs);
    setTimerPct(100);

    timerRef.current = setInterval(() => {
      if (!mountedRef.current) {
        stopTimer();
        return;
      }
      timerLeftRef.current -= 0.1;
      const pct = Math.max(0, (timerLeftRef.current / timerSecsRef.current) * 100);
      setTimerDisplay(Math.ceil(Math.max(0, timerLeftRef.current)));
      setTimerPct(pct);
      if (timerLeftRef.current <= 0) {
        stopTimer();
        if (!answeredRef.current) handleTimerExpire();
      }
    }, 100);
  }

  function handleTimerExpire() {
    if (answeredRef.current) return;
    answeredRef.current = true;
    const q = questionsRef.current[currentIdxRef.current];
    if (!q) return;
    setAnswerState({ chosen: null, correct: q.word.en, result: false });
    const newLives = doHandleWrong();
    setTimeout(() => {
      if (!mountedRef.current) return;
      if (newLives <= 0) doEndGame();
      else doNextQuestion();
    }, 700);
  }

  // ─────────────────────────────────────────────────────────
  //  CONFETTI
  // ─────────────────────────────────────────────────────────
  function fireConfetti() {
    const canvas = confettiRef.current;
    if (!canvas) return;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const ctx = canvas.getContext('2d');
    let pieces = Array.from({ length: 100 }, () => ({
      x: Math.random() * canvas.width,
      y: -10,
      r: Math.random() * 6 + 3,
      color: `hsl(${Math.random() * 360},90%,60%)`,
      dx: (Math.random() - 0.5) * 6,
      dy: Math.random() * 5 + 3,
      rot: Math.random() * 360,
      dr: Math.random() * 8 - 4,
      life: 1,
    }));
    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      pieces = pieces.filter((p) => p.life > 0);
      pieces.forEach((p) => {
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rot * Math.PI) / 180);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.life;
        ctx.fillRect(-p.r / 2, -p.r / 2, p.r, p.r);
        ctx.restore();
        p.x += p.dx;
        p.y += p.dy;
        p.rot += p.dr;
        p.dy += 0.1;
        p.life -= 0.012;
      });
      if (pieces.length > 0) requestAnimationFrame(animate);
    }
    animate();
  }

  // ─────────────────────────────────────────────────────────
  //  SPEECH
  // ─────────────────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────
  //  SPEECH (Blindado contra Bugs de Chrome)
  // ─────────────────────────────────────────────────────────
  function speakWord(word) {
    window.speechSynthesis?.cancel(); // Corta cualquier audio anterior
    
    const u = new SpeechSynthesisUtterance(word);
    u.lang = 'en-GB';
    u.rate = 0.85;
    u.pitch = 1;
    
    setIsSpeaking(true);

    // 🚀 HACK PARA CHROME: Evita que el navegador destruya el audio a la mitad
    window._speechUtteranceRef = u; 

    // Cuando termina de hablar normalmente
    u.onend = () => {
      if (mountedRef.current) setIsSpeaking(false);
    };

    // Si ocurre algún error o se corta abruptamente
    u.onerror = () => {
      if (mountedRef.current) setIsSpeaking(false);
    };

    window.speechSynthesis?.speak(u);
  }

  // ─────────────────────────────────────────────────────────
  //  FLASH + FLOAT SCORE
  // ─────────────────────────────────────────────────────────
  function doFlash(color) {
    setFlashColor(color);
    setTimeout(() => {
      if (mountedRef.current) setFlashColor(null);
    }, 250);
  }

  function showFloat(text, good) {
    const id = Date.now() + Math.random();
    setFloatScores((prev) => [...prev, { id, text, good }]);
    setTimeout(() => {
      if (mountedRef.current) setFloatScores((prev) => prev.filter((f) => f.id !== id));
    }, 900);
  }

  // ─────────────────────────────────────────────────────────
  //  START GAME
  // ─────────────────────────────────────────────────────────
  function startGame(modeId) {
    stopTimer();
    window.speechSynthesis?.cancel();
    const qs = buildQuestions(modeId, words);

    // Update refs synchronously so they're correct before any effect fires
    questionsRef.current = qs;
    currentIdxRef.current = 0;
    livesRef.current = 5;
    scoreRef.current = 0;
    streakRef.current = 0;
    maxStreakRef.current = 0;
    resultsRef.current = [];
    comboRef.current = 1;
    answeredRef.current = false;

    setQuestions(qs);
    setCurrentIndex(0);
    setScore(0);
    setLives(5);
    setStreak(0);
    setMaxStreak(0);
    setResults([]);
    setCombo(1);
    setCurrentModeId(modeId);
    setLastModeId(modeId);
    setAnswerState(null);
    setSpellValue('');
    setSpellFeedback({ text: '', ok: null });
    setSpellCorrect(false);
    setSpellShake(false);
    setIsSpeaking(false);
    // ✅ REEMPLAZADO: mic.reset() en lugar de los viejos setMicStatus/setMicResult
    // Nota: mic todavía no está definido aquí, pero lo inyectaremos más abajo.
    // Por ahora dejamos comentado porque el hook se inyecta después.
    // Cuando se ejecute startGame, mic ya existirá (el hook se declara antes del return).
    setTimerDisplay(5);
    setTimerPct(100);
    setScreen('game');
  }

  // ─────────────────────────────────────────────────────────
  //  SCORE / LIVES MUTATIONS
  //  (always update both ref and state so callbacks stay in sync)
  // ─────────────────────────────────────────────────────────
  function doHandleCorrect(mode) {
    const newStreak = streakRef.current + 1;
    const newMaxStreak = Math.max(maxStreakRef.current, newStreak);
    let pts = newStreak >= 5 ? 250 : newStreak >= 3 ? 175 : 100;
    let newCombo = comboRef.current;
    if (mode === 'lightning') {
      newCombo = Math.min(comboRef.current + 1, 8);
      pts *= newCombo;
    }
    const newScore = scoreRef.current + pts;
    const q = questionsRef.current[currentIdxRef.current];
    const newResult = { word: q.word, correct: true, mode: q.mode };

    streakRef.current = newStreak;
    maxStreakRef.current = newMaxStreak;
    scoreRef.current = newScore;
    comboRef.current = newCombo;
    resultsRef.current = [...resultsRef.current, newResult];

    setStreak(newStreak);
    setMaxStreak(newMaxStreak);
    setScore(newScore);
    setCombo(newCombo);
    setResults((prev) => [...prev, newResult]);

    doFlash('green');
    showFloat('+' + pts, true);
    return pts;
  }

  function doHandleWrong() {
    const newLives = Math.max(0, livesRef.current - 1);
    const q = questionsRef.current[currentIdxRef.current];
    const newResult = { word: q.word, correct: false, mode: q.mode };

    streakRef.current = 0;
    comboRef.current = 1;
    livesRef.current = newLives;
    resultsRef.current = [...resultsRef.current, newResult];

    setStreak(0);
    setCombo(1);
    setLives(newLives);
    setResults((prev) => [...prev, newResult]);

    doFlash('red');
    showFloat('-1 ❤️', false);
    return newLives;
  }

  // ─────────────────────────────────────────────────────────
  //  NAVIGATION
  // ─────────────────────────────────────────────────────────
  function doNextQuestion() {
    if (!mountedRef.current) return;
    const nextIdx = currentIdxRef.current + 1;
    if (nextIdx >= questionsRef.current.length) {
      doEndGame();
      return;
    }

    currentIdxRef.current = nextIdx;
    answeredRef.current = false;

    setCurrentIndex(nextIdx);
    setAnswerState(null);
    setSpellValue('');
    setSpellFeedback({ text: '', ok: null });
    setSpellCorrect(false);
    setSpellShake(false);
    // ✅ REEMPLAZADO: mic.reset() en lugar de los viejos setMicStatus/setMicResult
    stopTimer();
  }

  function doEndGame() {
    stopTimer();
    window.speechSynthesis?.cancel();
    const pct =
      resultsRef.current.length > 0
        ? Math.round(
            (resultsRef.current.filter((r) => r.correct).length / resultsRef.current.length) * 100
          )
        : 0;
    if (pct >= 85) setTimeout(fireConfetti, 200);
    setScreen('results');
  }

  // ─────────────────────────────────────────────────────────
  //  MCQ ANSWER
  // ─────────────────────────────────────────────────────────
  function checkAnswer(chosen, correct, mode) {
    if (answeredRef.current) return;
    answeredRef.current = true;
    stopTimer();
    const isCorrect = chosen === correct;
    setAnswerState({ chosen, correct, result: isCorrect });

    if (isCorrect) {
      doHandleCorrect(mode);
      setTimeout(() => {
        if (mountedRef.current) doNextQuestion();
      }, mode === 'lightning' ? 350 : 650);
    } else {
      const newLives = doHandleWrong();
      setTimeout(() => {
        if (!mountedRef.current) return;
        if (newLives <= 0) doEndGame();
        else doNextQuestion();
      }, 900);
    }
  }

  // ─────────────────────────────────────────────────────────
  //  SPELL CHECK
  // ─────────────────────────────────────────────────────────
  function checkSpell(correctWord) {
    if (answeredRef.current) return;
    answeredRef.current = true;
    const typed = spellValue.trim().toLowerCase();
    const correct = correctWord.toLowerCase();

    if (typed === correct) {
      const q = questionsRef.current[currentIdxRef.current];
      setSpellCorrect(true);
      setSpellFeedback({ text: '✓ Perfect!', ok: true });
      doHandleCorrect(q.mode);
      setTimeout(() => {
        if (mountedRef.current) doNextQuestion();
      }, 700);
    } else {
      setSpellShake(true);
      setSpellFeedback({ text: `✗ "${spellValue}" — correct: "${correctWord}"`, ok: false });
      setTimeout(() => {
        if (mountedRef.current) setSpellShake(false);
      }, 500);
      const newLives = doHandleWrong();
      setTimeout(() => {
        if (!mountedRef.current) return;
        if (newLives <= 0) doEndGame();
        else doNextQuestion();
      }, 1200);
    }
  }

  // ─────────────────────────────────────────────────────────
  //  GRADE
  // ─────────────────────────────────────────────────────────
  function getGrade() {
    const correct = results.filter((r) => r.correct).length;
    const total = results.length;
    const pct = total > 0 ? Math.round((correct / total) * 100) : 0;
    if (pct >= 95)
      return { grade: 'S+', color: '#fbbf24', label: 'LEGENDARY — Flawless Victory!', pct };
    if (pct >= 85)
      return { grade: 'A', color: '#22c55e', label: 'EXCELLENT — Almost Perfect!', pct };
    if (pct >= 70)
      return { grade: 'B', color: '#6366f1', label: 'GOOD — Solid Performance!', pct };
    if (pct >= 55)
      return { grade: 'C', color: '#f59e0b', label: 'OKAY — Keep Practicing!', pct };
    return { grade: 'F', color: '#f43f5e', label: "KEEP GOING — Don't Give Up!", pct };
  }

  // ─────────────────────────────────────────────────────────
  //  QUESTION RENDERERS  (plain functions, not React components,
  //  so no remount issues when called in JSX as {renderXxx(q)})
  // ─────────────────────────────────────────────────────────
  function renderMcqOptions(opts, correctEn, showEsLabel, mode) {
    return (
      <div className="mga-opts-grid">
        {opts.map((opt, i) => {
          let cls = 'mga-opt-btn';
          if (answerState) {
            if (opt.en === answerState.correct) cls += ' correct';
            else if (opt.en === answerState.chosen) cls += ' wrong';
          }
          return (
            <button
              key={opt.en + i}
              className={cls}
              disabled={!!answerState}
              onClick={() => checkAnswer(opt.en, correctEn, mode)}
            >
              <span className="mga-opt-letter">{'ABCD'[i]}</span>
              {showEsLabel ? opt.es : opt.en}
            </button>
          );
        })}
      </div>
    );
  }

  function renderListen(q) {
    return (
      <>
        <div className="mga-section-lbl">👂 What did you hear?</div>
        <button
          className={`mga-listen-btn${isSpeaking ? ' speaking' : ''}`}
          onClick={() => speakWord(q.word.en)}
          title="Click to play"
        >
          🔊
        </button>
        <div className="mga-listen-hint">
          Click the speaker to hear the word, then choose the translation
        </div>
        {renderMcqOptions(q.opts, q.word.en, true, q.mode)}
      </>
    );
  }

  function renderSniper(q) {
    const color = timerPct > 50 ? 'var(--ok)' : timerPct > 25 ? 'var(--wa)' : 'var(--er)';
    return (
      <>
        <div className="mga-timer-wrap">
          <div className="mga-timer-bar">
            <div className="mga-timer-fill" style={{ width: timerPct + '%', background: color }} />
          </div>
          <div className="mga-timer-num" style={{ color }}>
            {timerDisplay}
          </div>
        </div>
        <div className="mga-big-word">{q.word.es}</div>
        {renderMcqOptions(q.opts, q.word.en, false, q.mode)}
      </>
    );
  }

  function renderSpell(q) {
    const word = q.word.en;
    const typedNorm = spellValue.toLowerCase().replace(/ /g, '');
    let nonSpaceIdx = 0;
    const boxes = word.split('').map((c, i) => {
      if (c === ' ') return <span key={i} className="mga-hint-box mga-hint-space" />;
      const idx = nonSpaceIdx++;
      const revealed =
        idx === 0 ||
        (word.replace(/ /g, '').length > 5 && idx === word.replace(/ /g, '').length - 1) ||
        idx < typedNorm.length;
      return (
        <span key={i} className={`mga-hint-box${revealed ? ' known' : ''}`}>
          {c}
        </span>
      );
    });
    const locked = spellFeedback.ok !== null;
    return (
      <>
        <div className="mga-section-lbl">⌨️ Spell it perfectly</div>
        <div className="mga-big-word">{q.word.es}</div>
        {q.word.ph && <div className="mga-phonetic">[ {q.word.ph} ]</div>}
        <div className="mga-spell-hint-row">{boxes}</div>
        <div className="mga-spell-input-wrap">
          <input
              maxLength={LIMITES.respuestaMinijuego}
            ref={spellInputRef}
            className={`mga-spell-input${spellShake ? ' shake' : ''}${spellCorrect ? ' correct' : ''}`}
            type="text"
            value={spellValue}
            onChange={(e) => {
              if (!locked) setSpellValue(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !locked) {
                e.preventDefault();
                checkSpell(word);
              }
            }}
            placeholder="Type in English…"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            disabled={locked}
          />
          {spellFeedback.text && (
            <div
              className="mga-spell-feedback"
              style={{ color: spellFeedback.ok ? 'var(--ok)' : 'var(--er)' }}
            >
              {spellFeedback.text}
            </div>
          )}
          <button className="mga-spell-submit" onClick={() => checkSpell(word)} disabled={locked}>
            ✓ Check
          </button>
        </div>
      </>
    );
  }

  // ✅ NUEVA FUNCIÓN renderMic: usa el componente MicDisplay
  function renderMic(q) {
    return (
      <MicDisplay
        q={q}
        mic={mic}
        onSkipCorrect={() => {
          mic.reset();
          doHandleCorrect(q.mode);
          setTimeout(() => doNextQuestion(), 600);
        }}
        onSkipWrong={() => {
          mic.reset();
          const newLives = doHandleWrong();
          setTimeout(() => {
            newLives <= 0 ? doEndGame() : doNextQuestion();
          }, 600);
        }}
      />
    );
  }

  function renderLightning(q) {
    return (
      <>
        <div className="mga-combo-display">{combo > 1 ? `x${combo} COMBO!` : <span>&nbsp;</span>}</div>
        <div className="mga-section-lbl">⚡ LIGHTNING</div>
        <div className="mga-lightning-word">{q.word.es}</div>
        {q.word.ph && (
          <div className="mga-phonetic" style={{ margin: '.4rem 0' }}>
            [ {q.word.ph} ]
          </div>
        )}
        <div className="mga-lightning-grid">
          {q.opts.map((opt, i) => {
            let cls = 'mga-lightning-opt';
            if (answerState) {
              if (opt.en === answerState.correct) cls += ' correct';
              else if (opt.en === answerState.chosen) cls += ' wrong';
            }
            return (
              <button
                key={opt.en + i}
                className={cls}
                disabled={!!answerState}
                onClick={() => checkAnswer(opt.en, q.word.en, q.mode)}
              >
                {opt.en}
              </button>
            );
          })}
        </div>
      </>
    );
  }

  function renderQuestion() {
    const q = questions[currentIndex];
    if (!q) return null;
    const mi = MODES.find((m) => m.id === q.mode) || MODES[0];
    return (
      <div className="mga-q-inner">
        <div className="mga-mode-indicator" style={{ color: mi.color }}>
          {mi.icon} {mi.name.toUpperCase()}
        </div>
        {q.mode === 'listen' && renderListen(q)}
        {q.mode === 'sniper' && renderSniper(q)}
        {q.mode === 'spell' && renderSpell(q)}
        {q.mode === 'mic' && renderMic(q)}
        {q.mode === 'lightning' && renderLightning(q)}
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────
  //  DERIVED VALUES
  // ─────────────────────────────────────────────────────────
  const modeInfo = MODES.find((m) => m.id === currentModeId);
  const progPct = questions.length > 0 ? Math.round((currentIndex / questions.length) * 100) : 0;
  const correctCount = results.filter((r) => r.correct).length;
  const { grade, color: gradeColor, label: gradeLabel, pct: gradePct } =
    screen === 'results' ? getGrade() : { grade: '', color: '', label: '', pct: 0 };

  // ✅ INYECTAR EL HOOK justo antes del return
  const mic = useMicSystem({
    questionsRef,
    currentIdxRef,
    currentIndex, // 👈 ¡SUTURA 3: Pásale el índice de la pregunta!
    livesRef,
    mountedRef,
    onCorrect: doHandleCorrect,
    onWrong: doHandleWrong,
    onNext: doNextQuestion,
    onEnd: doEndGame,
    onConfetti: fireConfetti,
  });

  // ─────────────────────────────────────────────────────────
  //  RENDER
  // ─────────────────────────────────────────────────────────
  return (
    <div className={`mga-overlay${isDark ? ' dark' : ''}`}>
      {/* ── background layers ── */}
      <canvas ref={particlesRef} className="mga-canvas-bg" />
      <canvas ref={confettiRef} className="mga-canvas-confetti" />

      {/* ── feedback overlays ── */}
      {flashColor && <div className={`mga-flash ${flashColor}`} />}
      {floatScores.map((f) => (
        <div key={f.id} className={`mga-float-score${f.good ? ' good' : ' miss'}`}>
          {f.text}
        </div>
      ))}

      {/* ── main container ── */}
      <div className="mga-app">
        {/* CLOSE — always visible */}
        <div className="mga-top-controls">
          <button className="mga-icon-btn close-btn" onClick={onClose} title="Close game">
            ✕
          </button>
        </div>

        {/* ════════════ HOME ════════════ */}
        {screen === 'home' && (
          <div className="mga-screen sc-in">
            <div className="mga-hero">
              <div className="mga-hero-tag">⚔️ Master Vocabulary · A1→C2</div>
              <h1 className="mga-h1">
                <span className="mga-glow">FLUENCY</span>
                <span className="mga-ac">NEXUS</span>
              </h1>
              <p className="mga-hero-sub">
                5 epic game modes. Train your brain like a professional athlete.
              </p>
              <div className="mga-stats-row">
                {['👂 Bionic Ear', '👁️ Sniper Vision', '⌨️ Muscle Memory', '🎙️ Pronunciation', '⚡ Lightning'].map(
                  (p) => (
                    <span key={p} className="mga-stat-pill">
                      {p}
                    </span>
                  )
                )}
              </div>
              <div className="mga-word-count">
                Playing with <strong>{words.length}</strong> words ·{' '}
                <strong>{Math.min(words.length, 20)}</strong> drawn per round
              </div>
            </div>

            <div className="mga-modes-grid">
              {MODES.map((m) => (
                <button
                  key={m.id}
                  className={`mga-mode-card${m.isMix ? ' mix' : ''}`}
                  style={{ '--mc': m.color }}
                  onClick={() => {
                    // Al iniciar juego, también reseteamos el micrófono
                    mic.reset();
                    startGame(m.id);
                  }}
                >
                  <div className="mga-card-glow" />
                  <span className="mga-card-icon">{m.icon}</span>
                  <div className="mga-card-phase">{m.phase}</div>
                  <div className="mga-card-name">{m.name}</div>
                  <div className="mga-card-desc">{m.desc}</div>
                  <div className="mga-card-tags">
                    {m.tags.map((t) => (
                      <span key={t} className="mga-card-tag">
                        {t}
                      </span>
                    ))}
                  </div>
                  <div className="mga-card-arrow">→</div>
                  <div className="mga-card-bar" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ════════════ GAME ════════════ */}
        {screen === 'game' && (
          <div className="mga-screen sc-in">
            <div className="mga-game-topbar">
              <button
                className="mga-back-btn"
                onClick={() => {
                  stopTimer();
                  window.speechSynthesis?.cancel();
                  mic.reset(); // Limpiar mic al salir
                  setScreen('home');
                }}
              >
                ← Back
              </button>

              {modeInfo && (
                <span
                  className="mga-mode-badge"
                  style={{
                    background: `color-mix(in srgb,${modeInfo.color} 14%,var(--bg))`,
                    color: modeInfo.color,
                  }}
                >
                  {modeInfo.icon} {modeInfo.name}
                </span>
              )}

              <div className="mga-spacer" />
              <div className={`mga-streak-badge${streak > 0 ? ' pulse' : ''}`}>🔥 {streak}</div>
              <div className="mga-score-badge">{score}</div>
              <div className="mga-lives-row">
                {Array.from({ length: 5 }, (_, i) => (i < lives ? '❤️' : '🖤')).join('')}
              </div>
            </div>

            <div className="mga-progress-wrap">
              <div className="mga-progress-bar">
                <div className="mga-progress-fill" style={{ width: progPct + '%' }} />
              </div>
              <div className="mga-progress-meta">
                <span>
                  {currentIndex} / {questions.length}
                </span>
                <span>{progPct}%</span>
              </div>
            </div>

            <div className="mga-q-area">{renderQuestion()}</div>
          </div>
        )}

        {/* ════════════ RESULTS ════════════ */}
        {screen === 'results' && (
          <div className="mga-screen sc-in mga-results-wrap">
            <div
              className="mga-grade-display"
              style={{
                color: gradeColor,
                textShadow: isDark
                  ? `0 0 60px ${gradeColor}, 0 0 120px ${gradeColor}40`
                  : 'none',
              }}
            >
              {grade}
            </div>
            <div className="mga-grade-label">{gradeLabel}</div>

            <div className="mga-stats-cards">
              {[
                { num: score, lbl: 'Total Score', c: 'var(--ac)' },
                { num: `${correctCount}/${results.length}`, lbl: 'Correct', c: 'var(--ok)' },
                { num: `${gradePct}%`, lbl: 'Accuracy', c: gradeColor },
                { num: maxStreak, lbl: 'Best Streak', c: 'var(--wa)' },
              ].map((s) => (
                <div key={s.lbl} className="mga-stat-card">
                  <div className="mga-stat-num" style={{ color: s.c }}>
                    {s.num}
                  </div>
                  <div className="mga-stat-lbl">{s.lbl}</div>
                </div>
              ))}
            </div>

            <div className="mga-review-header">📋 Question Review</div>
            <div className="mga-review-list">
              {results.map((r, i) => {
                const mi = MODES.find((m) => m.id === r.mode);
                return (
                  <div key={i} className="mga-review-item">
                    <div className={`mga-review-icon${r.correct ? ' ok' : ' miss'}`}>
                      {r.correct ? '✓' : '✗'}
                    </div>
                    <div className="mga-review-en">{r.word.en}</div>
                    <div className="mga-review-es">{r.word.es}</div>
                    <span
                      className="mga-review-mode"
                      style={{
                        background: `color-mix(in srgb,${mi?.color || 'var(--ac)'} 12%,var(--bg))`,
                        color: mi?.color || 'var(--ac)',
                      }}
                    >
                      {mi ? `${mi.icon} ${mi.id.toUpperCase()}` : r.mode}
                    </span>
                  </div>
                );
              })}
            </div>

            <button
              className="mga-restart-btn"
              onClick={() => {
                mic.reset(); // 👈 Limpia el micrófono
                startGame(lastModeId); // 👈 Inicia un juego nuevo
              }}
            >
              🔄 Play Again (same mode)
            </button>
            <button
              className="mga-home-btn"
              onClick={() => {
                mic.reset();
                setScreen('home');
              }}
            >
              🏠 Back to Menu
            </button>
          </div>
        )}
      </div>
    </div>
  );
}